import React, {FC, ReactNode, useCallback, useEffect} from 'react';
import {Observable, Subject} from 'rxjs';
import styled from 'styled-components';

import {ComposerAiFeature} from '../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  useConstant,
  useObservableRef,
  usePrevious,
} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {MessageComposerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/composerDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {useLayerEffect} from '../../../../../../libs/shared/ui/src/components/layers/layerListenerContext';
import {RepositionPopover} from '../../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {shadows} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useFormattingPopoverAnchor} from '../../../../common/editor/formattingPopover/formattingPopoverAnchor';
import {ContentEditableHtmlEditor} from '../../../../common/editor/html/contentEditableHtmlEditor';
import {
  composerBaseScrollableDropzoneClassName,
  timelineScrollerClassName,
} from '../../composerCommonClassnames';
import {AiComposeRefineDropdown} from './aiComposeRefineDropdown';

/*
 * Props.
 */

interface EmailComposeFloatingToolbarProps {
  onAiFeature: (feature: ComposerAiFeature) => void;
  formattingToolbar?: ReactNode;
  isDisabled: boolean;
  editor: ContentEditableHtmlEditor;
  editorElement$: Observable<HTMLElement | null>;
  onAiMenuOpen: () => void;
}

/*
 * Style.
 */

const StyledContentWrapperDiv = styled.div`
  ${shadows.dialogsAndDropdowns};
  background: ${(p) => p.theme.foreground};
  border-radius: 8px;
  overflow: hidden;
  padding-left: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 6px;
`;

/*
 * Component.
 */

const scrollClassnames = [timelineScrollerClassName, composerBaseScrollableDropzoneClassName];

export const EmailComposerFloatingToolbar: FC<EmailComposeFloatingToolbarProps> = (props) => {
  const theme = useUserTheme();
  const {formattingToolbar, isDisabled, editor, editorElement$} = props;
  const [contentWrapperRef, contentWrapper$] = useObservableRef<HTMLDivElement>();
  const {selectedText} = editor.services.selection;
  const shouldIgnoreBlurredState$ = useConstant(() => new Subject<boolean>());

  useLayerEffect(() => {
    shouldIgnoreBlurredState$.next(true);

    return () => {
      shouldIgnoreBlurredState$.next(false);
    };
  });

  const [anchor, dismissAnchor] = useFormattingPopoverAnchor(
    editor.services.selection,
    editorElement$,
    contentWrapper$,
    shouldIgnoreBlurredState$,
    scrollClassnames,
  );

  const onRequestClose = useCallback(() => {
    dismissAnchor();
    editor.services.focus.focus();
  }, [dismissAnchor, editor]);

  const {trackDataEvent} = useDataEventTracking();
  const previousAnchor = usePrevious(anchor);
  const previousSelectedText = usePrevious(selectedText);

  useEffect(() => {
    if (!anchor || !selectedText || !formattingToolbar) {
      return;
    }

    if (anchor === previousAnchor || selectedText === previousSelectedText) {
      return;
    }

    trackDataEvent(DataEventActionsEnum.VIEW, MessageComposerDataEventObject.FLOATING_TOOLBAR);
  }, [anchor, formattingToolbar, previousAnchor, previousSelectedText, selectedText, trackDataEvent]);

  if (!anchor || !selectedText) {
    return null;
  }

  const AiDropdownBox = (
    <DropdownBoxCoordinator
      isDisabled={isDisabled}
      size={VisualSizesEnum.MEDIUM}
      shouldDisablePadding
      leftIcon="sparklesAi"
      leftIconColor={theme.greys.shade80}
      value={<Trans id="ai-compose-floating-button-alt">Polish</Trans>}
      shouldPreventMouseDown
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          props.onAiMenuOpen();
        }
      }}
      renderDropdown={(popoverTransferProps) => (
        <AiComposeRefineDropdown
          isExclusive
          shouldHideHeader
          excludeFeatures={[]}
          popoverTransferProps={popoverTransferProps}
          onAiFeature={(feat) => {
            popoverTransferProps.onRequestClose();
            props.onAiFeature(feat);
          }}
        />
      )}
    />
  );

  if (formattingToolbar) {
    return (
      <RepositionPopover
        anchor={() => anchor.bounds}
        isExclusive={false}
        placement="top"
        onRequestClose={onRequestClose}
      >
        <StyledContentWrapperDiv ref={contentWrapperRef}>
          {AiDropdownBox}
          {formattingToolbar}
        </StyledContentWrapperDiv>
      </RepositionPopover>
    );
  }

  return (
    <RepositionPopover anchor={() => anchor.bounds} isExclusive={false} placement="bottom-start">
      <div ref={contentWrapperRef}>{AiDropdownBox}</div>
    </RepositionPopover>
  );
};
